// zr-core/callUx — the framework-agnostic UX specification for the SDK's native call UI.
//
// Two familiar experiences, one engine:
//   • 'whatsapp'    — Secure P2P 1:1 (E2EE). Fullscreen remote + a small self picture-in-
//                     picture, a compact round control bar, and a slide-to-answer incoming
//                     screen. This is what users know from a WhatsApp voice/video call.
//   • 'google-meet' — P2P Mesh Room + Managed Conference. A tiled participant grid with an
//                     active-speaker frame and the full Meet control rail (mic, camera,
//                     present, reactions, raise hand, participants, leave).
//
// This module is PURE (no DOM) so the SAME spec drives the web UI kit (adapters/web) AND a
// native renderer (React Native / CTV), keeping web-to-native and native-to-web identical.
// The DOM/native layer only reads these declarations and wires each control id to the
// matching ZrClient method.

import type { CallMode } from './index';

export type CallUxStyle = 'whatsapp' | 'google-meet';
/** What a developer sets; `auto` picks the style from the call shape. */
export type CallUxChoice = CallUxStyle | 'auto';

/** How the participant video is laid out for a given style. */
export type CallLayout = 'fullscreen-pip' | 'grid';

export interface CallControlSpec {
  /** Stable id the renderer maps to a ZrClient method (mic→setMicEnabled, end→hangup …). */
  id:
    | 'mic'
    | 'camera'
    | 'speaker'
    | 'flip-camera'
    | 'present'
    | 'reactions'
    | 'raise-hand'
    | 'participants'
    | 'end';
  label: string;
  /** A default glyph (emoji) so the kit renders with zero icon deps; overridable by the app. */
  icon: string;
  /** toggle = on/off state; action = fire-and-forget; end = the hang-up/leave affordance. */
  kind: 'toggle' | 'action' | 'end';
  /** danger paints the control red (end/leave). */
  danger?: boolean;
}

/** The incoming-call answer controls (WhatsApp-style accept/decline). */
export const INCOMING_CONTROLS: CallControlSpec[] = [
  { id: 'end', label: 'Decline', icon: '📵', kind: 'end', danger: true },
  { id: 'mic', label: 'Accept', icon: '📞', kind: 'action' }, // renderer maps Accept→client.accept()
];

/**
 * WhatsApp in-call bar — the familiar set for both 1:1 and the group/mesh "watch party"
 * (Video, Microphone, Share Screen, emoticons/reactions, End Call). Same controls whether
 * the layout is fullscreen-PiP (1:1) or a grid (mesh up to 10), so the experience stays
 * consistent as people join.
 */
const WHATSAPP_CONTROLS: CallControlSpec[] = [
  { id: 'reactions', label: 'React', icon: '😊', kind: 'action' },
  { id: 'present', label: 'Share', icon: '🖥️', kind: 'action' },
  { id: 'camera', label: 'Video', icon: '📹', kind: 'toggle' },
  { id: 'mic', label: 'Mute', icon: '🎙️', kind: 'toggle' },
  { id: 'end', label: 'End', icon: '📞', kind: 'end', danger: true },
];

/** Google Meet mesh/conference rail — the full control set. */
const MEET_CONTROLS: CallControlSpec[] = [
  { id: 'mic', label: 'Mic', icon: '🎙️', kind: 'toggle' },
  { id: 'camera', label: 'Camera', icon: '📹', kind: 'toggle' },
  { id: 'present', label: 'Present', icon: '🖥️', kind: 'action' },
  { id: 'reactions', label: 'React', icon: '😊', kind: 'action' },
  { id: 'raise-hand', label: 'Raise', icon: '✋', kind: 'toggle' },
  { id: 'participants', label: 'People', icon: '👥', kind: 'action' },
  { id: 'end', label: 'Leave', icon: '📞', kind: 'end', danger: true },
];

export const CONTROL_SETS: Record<CallUxStyle, CallControlSpec[]> = {
  whatsapp: WHATSAPP_CONTROLS,
  'google-meet': MEET_CONTROLS,
};

/** The full presentation spec the renderer consumes for a resolved style. */
export interface CallUxSpec {
  style: CallUxStyle;
  layout: CallLayout;
  controls: CallControlSpec[];
  incomingControls: CallControlSpec[];
  /** Show the "🔒 end-to-end encrypted" banner (WhatsApp secure 1:1). */
  showE2eeBanner: boolean;
  /** Show the call-duration timer in the header (WhatsApp). */
  showCallTimer: boolean;
  /** Show the live signal/quality HUD chip (Meet shows network quality per tile). */
  showQualityHud: boolean;
  /** Human label for the experience, for UI + codegen. */
  displayName: string;
}

/**
 * Resolve the concrete UX style. `auto` (the default) maps the call shape to the familiar
 * experience: a 2-person `call` room → WhatsApp; a mesh `group` / larger / `broadcast`
 * room → Google Meet. An explicit choice always wins.
 */
export function resolveCallUx(input: {
  style?: CallUxChoice;
  mode?: CallMode;
  maxParticipants?: number;
}): CallUxStyle {
  const choice = input.style ?? 'auto';
  if (choice === 'whatsapp' || choice === 'google-meet') return choice;
  // auto
  const isOneToOne = (input.mode ?? 'group') === 'call' && (input.maxParticipants ?? 2) <= 2;
  return isOneToOne ? 'whatsapp' : 'google-meet';
}

/** Build the full presentation spec for a resolved (or resolvable) style. */
export function callUxSpec(input: {
  style?: CallUxChoice;
  mode?: CallMode;
  maxParticipants?: number;
}): CallUxSpec {
  const style = resolveCallUx(input);
  if (style === 'whatsapp') {
    // WhatsApp is 1:1 fullscreen+PiP, but a WhatsApp group/mesh "watch party" (>2, up to 10)
    // uses a tiled grid while keeping the same WhatsApp-round controls + E2EE banner.
    const oneToOne = (input.maxParticipants ?? 2) <= 2 && (input.mode ?? 'call') === 'call';
    return {
      style,
      layout: oneToOne ? 'fullscreen-pip' : 'grid',
      controls: CONTROL_SETS.whatsapp,
      incomingControls: INCOMING_CONTROLS,
      showE2eeBanner: true,
      showCallTimer: true,
      showQualityHud: false,
      displayName: oneToOne
        ? 'WhatsApp-style Secure Call (1:1, E2EE)'
        : 'WhatsApp-style Secure Mesh (Watch Party, ≤10, E2EE)',
    };
  }
  return {
    style,
    layout: 'grid',
    controls: CONTROL_SETS['google-meet'],
    incomingControls: INCOMING_CONTROLS,
    showE2eeBanner: false,
    showCallTimer: false,
    showQualityHud: true,
    displayName: 'Google Meet-style Room & Conference',
  };
}

/** Format elapsed call seconds as m:ss / h:mm:ss for the WhatsApp header timer (pure). */
export function formatCallDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const mm = hrs > 0 ? String(mins).padStart(2, '0') : String(mins);
  const ss = String(secs).padStart(2, '0');
  return hrs > 0 ? `${hrs}:${mm}:${ss}` : `${mm}:${ss}`;
}
